﻿import React, { useCallback, useEffect, useState } from 'react'
import { Heading, Button, Input, Text, Select, LoadingSpinner } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { modulesService } from '../../services/modules'
import { emailTemplatesService } from '../../services/emailTemplates'
import {
  EMAIL_SYSTEM_LABELS,
  EMAIL_SYSTEM_OPTIONS,
  EmailSystem,
  EmailTemplate,
  normalizeEmailSystem,
} from '../../types/emailTemplates'
import {
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { EmailDispatchesTab } from './EmailDispatchesTab'

const MODULE_NAME = 'configuracoes_email'
type EmailTab = 'modelos' | 'envios'
const LOG_MODULE_LABEL = 'Configurações • E-mail'

function templateLogFields(row: {
  id?: number
  subject: string
  sender: string
  system: string
  html_storage_path?: string
  obs?: string | null
}) {
  return {
    id: row.id ?? null,
    subject: row.subject,
    sender: row.sender,
    system: normalizeEmailSystem(row.system),
    html_storage_path: row.html_storage_path ?? null,
    obs: row.obs ?? null,
  }
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function isValidEmail(value: string): boolean {
  return EMAIL_REGEX.test(value.trim())
}

function isHtmlFile(file: File): boolean {
  const name = file.name.toLowerCase()
  return (
    name.endsWith('.html') ||
    name.endsWith('.htm') ||
    file.type === 'text/html' ||
    file.type === 'application/xhtml+xml'
  )
}

const ConfiguracoesEmail: React.FC = () => {
  const [activeTab, setActiveTab] = useState<EmailTab>('modelos')
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')
  const { logCreate, logUpdate, logDelete } = useUserLogs()

  const [moduleTitle, setModuleTitle] = useState('Configurações • E-mail')
  const [moduleDescription, setModuleDescription] = useState('Modelos de e-mail HTML')

  const [items, setItems] = useState<EmailTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [filterSystem, setFilterSystem] = useState<EmailSystem | ''>('')
  const [message, setMessage] = useState<string | null>(null)
  const [messageType, setMessageType] = useState<'success' | 'error' | null>(null)

  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editing, setEditing] = useState<EmailTemplate | null>(null)
  const [subject, setSubject] = useState('')
  const [sender, setSender] = useState('')
  const [system, setSystem] = useState<EmailSystem>('shop')
  const [obs, setObs] = useState('')
  const [htmlFile, setHtmlFile] = useState<File | null>(null)
  const [saving, setSaving] = useState(false)

  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewTitle, setPreviewTitle] = useState('')

  useEffect(() => {
    modulesService.getByName(MODULE_NAME).then((result) => {
      if (result.success && result.data) {
        if (result.data.display_name) setModuleTitle(result.data.display_name)
        if (result.data.description) setModuleDescription(result.data.description)
      }
    })
  }, [])

  const showMessage = (text: string, type: 'success' | 'error') => {
    setMessage(text)
    setMessageType(type)
    window.setTimeout(() => {
      setMessage(null)
      setMessageType(null)
    }, 5000)
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await emailTemplatesService.list({
        system: filterSystem || undefined,
      })
      setItems(data)
    } catch (e: any) {
      showMessage(e?.message || 'Erro ao carregar modelos', 'error')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [filterSystem])

  useEffect(() => {
    load()
  }, [load])

  const openNew = () => {
    setEditing(null)
    setSubject('')
    setSender('')
    setSystem('shop')
    setObs('')
    setHtmlFile(null)
    setIsFormOpen(true)
  }

  const openEdit = (row: EmailTemplate) => {
    setEditing(row)
    setSubject(row.subject)
    setSender(row.sender)
    setSystem(normalizeEmailSystem(row.system))
    setObs(row.obs ?? '')
    setHtmlFile(null)
    setIsFormOpen(true)
  }

  const closeForm = () => {
    setIsFormOpen(false)
    setEditing(null)
    setHtmlFile(null)
  }

  const save = async () => {
    if (!subject.trim()) {
      showMessage('Informe o assunto.', 'error')
      return
    }
    if (!sender.trim() || !isValidEmail(sender)) {
      showMessage('Informe um remetente com e-mail válido.', 'error')
      return
    }
    if (!editing && !htmlFile) {
      showMessage('Selecione um arquivo HTML.', 'error')
      return
    }
    if (htmlFile && !isHtmlFile(htmlFile)) {
      showMessage('O arquivo deve ser .html ou .htm.', 'error')
      return
    }

    setSaving(true)
    try {
      if (editing) {
        const oldValues = templateLogFields(editing)
        const updated = await emailTemplatesService.update(editing.id, {
          subject,
          sender,
          system,
          file: htmlFile,
          obs,
        })
        await logUpdate(
          'email_template',
          String(editing.id),
          `Atualizou modelo de e-mail #${editing.id}: ${updated.subject}`,
          oldValues,
          {
            ...templateLogFields(updated),
            html_file_replaced: !!htmlFile,
          },
          { additional_info: LOG_MODULE_LABEL }
        )
        showMessage('Modelo atualizado com sucesso.', 'success')
      } else {
        const created = await emailTemplatesService.create({
          subject,
          sender,
          system,
          file: htmlFile!,
          obs,
        })
        await logCreate(
          'email_template',
          String(created.id),
          `Criou modelo de e-mail #${created.id}: ${created.subject}`,
          {
            additional_info: LOG_MODULE_LABEL,
            new_values: templateLogFields(created),
          }
        )
        showMessage('Modelo criado com sucesso.', 'success')
      }
      closeForm()
      await load()
    } catch (e: any) {
      showMessage(e?.message || 'Erro ao salvar modelo', 'error')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (row: EmailTemplate) => {
    if (
      !window.confirm(
        `Excluir o modelo #${row.id} (${row.subject})? A exclusão é lógica: o registro deixa de aparecer na lista, mas permanece no banco com o usuário e a data da exclusão.`
      )
    ) {
      return
    }
    try {
      await emailTemplatesService.remove(row.id)
      await logDelete(
        'email_template',
        String(row.id),
        `Excluiu modelo de e-mail #${row.id}: ${row.subject}`,
        {
          additional_info: `${LOG_MODULE_LABEL} (exclusão lógica)`,
          old_values: templateLogFields(row),
        }
      )
      showMessage('Modelo excluído.', 'success')
      await load()
    } catch (e: any) {
      showMessage(e?.message || 'Erro ao excluir', 'error')
    }
  }

  const openPreview = async (row: EmailTemplate) => {
    setPreviewOpen(true)
    setPreviewTitle(`#${row.id} — ${row.subject}`)
    setPreviewHtml(null)
    setPreviewLoading(true)
    try {
      const html = await emailTemplatesService.downloadHtml(row.html_storage_path)
      setPreviewHtml(html)
    } catch (e: any) {
      showMessage(e?.message || 'Erro ao carregar preview', 'error')
      setPreviewOpen(false)
    } finally {
      setPreviewLoading(false)
    }
  }

  const closePreview = () => {
    setPreviewOpen(false)
    setPreviewHtml(null)
    setPreviewTitle('')
  }

  const tabButtonClass = (active: boolean) =>
    `py-4 px-1 border-b-2 font-medium text-sm ${
      active
        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
    }`

  return (
    <>
    <main className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto bg-gray-100 dark:bg-gray-900">
        <PermissionGuard
          moduleName={MODULE_NAME}
          permissionType="show"
          fallback={
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Text size="xl" className="text-gray-500 dark:text-gray-400 mb-2">
                  Acesso Negado
                </Text>
                <Text size="sm" color="muted">
                  Você não tem permissão para acessar esta página.
                </Text>
              </div>
            </div>
          }
        >
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-8">
              <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
                {moduleTitle}
              </Heading>
              <Text size="sm" color="muted" className="mt-2">
                {moduleDescription}
              </Text>
            </div>

            <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                <button
                  type="button"
                  className={tabButtonClass(activeTab === 'modelos')}
                  onClick={() => setActiveTab('modelos')}
                >
                  Modelos
                </button>
                <button
                  type="button"
                  className={tabButtonClass(activeTab === 'envios')}
                  onClick={() => setActiveTab('envios')}
                >
                  Envios
                </button>
              </nav>
            </div>

            {activeTab === 'modelos' && (
              <>
            {message && (
              <div
                className={`mb-4 rounded-lg px-4 py-3 text-sm ${
                  messageType === 'success'
                    ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                    : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                }`}
              >
                {message}
              </div>
            )}

            <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="w-full sm:max-w-xs">
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
                  Filtrar por sistema
                </Text>
                <Select
                  value={filterSystem}
                  onChange={(e) => setFilterSystem(e.target.value as EmailSystem | '')}
                >
                  <option value="">Todos</option>
                  {EMAIL_SYSTEM_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </div>
              <Text size="sm" color="muted">
                {items.length} modelo(s)
              </Text>
              {canInsert && (
                <Button variant="solid" onClick={openNew} className="shrink-0">
                  <PlusIcon className="h-4 w-4 mr-1.5 inline" />
                  Novo modelo
                </Button>
              )}
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/30">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assunto</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remetente</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sistema</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Obs</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center">
                        <LoadingSpinner />
                      </td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
                        Nenhum modelo cadastrado.
                      </td>
                    </tr>
                  ) : (
                    items.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-3 text-sm font-mono text-gray-900 dark:text-white">{row.id}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{row.subject}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{row.sender}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                          {EMAIL_SYSTEM_LABELS[row.system]}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-xs truncate" title={row.obs || undefined}>
                          {row.obs?.trim() || '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              title="Visualizar"
                              onClick={() => openPreview(row)}
                              className="p-1 text-gray-400 hover:text-primary-500 dark:hover:text-primary-400"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            {canUpdate && (
                              <button
                                type="button"
                                title="Editar"
                                onClick={() => openEdit(row)}
                                className="p-1 text-gray-400 hover:text-primary-500 dark:hover:text-primary-400"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                title="Excluir"
                                onClick={() => remove(row)}
                                className="p-1 text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
              </>
            )}

            {activeTab === 'envios' && <EmailDispatchesTab />}
          </div>
        </PermissionGuard>
      </main>

      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <Text size="lg" className="font-semibold text-gray-900 dark:text-white">
                {editing ? 'Editar modelo' : 'Novo modelo'}
              </Text>
              <button type="button" onClick={closeForm} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Assunto *</Text>
                <Input value={subject} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)} placeholder="Assunto do e-mail" />
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Remetente *</Text>
                <Input type="email" value={sender} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSender(e.target.value)} placeholder="remetente@exemplo.com" />
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Sistema *</Text>
                <Select value={system} onChange={(e) => setSystem(e.target.value as EmailSystem)}>
                  {EMAIL_SYSTEM_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Obs</Text>
                <textarea
                  rows={3}
                  value={obs}
                  onChange={(e) => setObs(e.target.value)}
                  placeholder="Observação opcional"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y min-h-[72px]"
                />
              </div>
              <div>
                <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">
                  Arquivo HTML {editing ? '(opcional)' : '*'}
                </Text>
                <input
                  type="file"
                  accept=".html,.htm,text/html"
                  onChange={(e) => setHtmlFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-gray-600 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 dark:file:bg-primary-900/30 dark:file:text-primary-300"
                />
                {htmlFile && (
                  <Text size="xs" color="muted" className="mt-1">{htmlFile.name}</Text>
                )}
                {editing && !htmlFile && (
                  <Text size="xs" color="muted" className="mt-1">Mantém o arquivo atual se nenhum for selecionado.</Text>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={closeForm} disabled={saving}>Cancelar</Button>
              <Button variant="solid" onClick={save} disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-4xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <Text size="lg" className="font-semibold text-gray-900 dark:text-white truncate pr-4">
                {previewTitle}
              </Text>
              <button type="button" onClick={closePreview} className="text-gray-400 hover:text-gray-600 shrink-0">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 min-h-[300px] p-4 overflow-auto bg-gray-50 dark:bg-gray-950">
              {previewLoading ? (
                <div className="flex items-center justify-center h-full">
                  <LoadingSpinner />
                </div>
              ) : previewHtml ? (
                <iframe
                  title="Preview do modelo"
                  className="w-full h-[min(70vh,600px)] border border-gray-200 dark:border-gray-700 rounded bg-white"
                  sandbox=""
                  srcDoc={previewHtml}
                />
              ) : (
                <Text color="muted">Conteúdo indisponível.</Text>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ConfiguracoesEmail
